import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';

import { makeStyles, useTheme } from '@mui/styles';
import { alpha } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';

import {
    zoom as D3zoom,
    select as D3select,
    scaleTime as D3scaleTime,
    axisBottom as D3axisBottom,
} from 'd3';
import * as d3Time from 'd3-time';
import { get as _get } from 'lodash';
import Moment from 'moment';

import useWindowSize from '../../../libraries/usewindowsize';
import { teal, blue, pumpkin, olive, purple, lavender } from '../../../data/colors';
import DataContext from '../../../data/data-context';
import Mitre from '../../../components/mitre';
import Auth from '../../../data/auth-service';
import StyledPaper from '../../../components/styled/paper';
import StyledTooltip from '../../../components/styled/tooltip';
import { NewNote } from './notes';

const MARGIN = 20;
const AXISMARGIN = 53;
const HEIGHT = 85;
const BASE_ZINDEX = 500;
const AXIS_THICKNESS = 107;
const FORGROUND_THICKNESS = 25;

const useStyles = makeStyles(theme => ({
    root: {
        marginTop: MARGIN,
        position: 'relative',
        overflow: 'hidden',
    },
    nodes: {
        position: 'relative',
        height: HEIGHT,
    },
    nodeText: {
        marginLeft: 10,
        position: 'absolute',
        width: 90,
        maxHeight: 30,
        overflow: 'hidden',
        backgroundColor: `${alpha(theme.palette.background.default, 0.8)}`,
    },
    nodeCategory: {
        position: 'absolute',
        zIndex: BASE_ZINDEX + 2,
        fontSize: 'smaller',
        paddingLeft: 5,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        color: theme.palette.getContrastText(purple[300]),
        pointerEvents: 'none',

        '&.hide': {
            display: 'none',
        }
    },
    categoryStem: {
        stroke: lavender[400],
        strokeWidth: 3,
        pointerEvents: 'none',
    },
    nodeLine: {
        position: 'absolute',
        width: 5,
    },
    nodeMarker: {
        position: 'absolute',
        fontSize: 15,
        zIndex: BASE_ZINDEX + 2,
    },
    axisContainer: {
        position: 'absolute',
        top: HEIGHT,
    },
    axis: {
        '& .x-axis path': {
            display: 'none',
        },
        '& .x-axis line': {
            display: 'none',
        },
        '& .x-axis text': {
            fill: 'black',
            fontWeight: 'bold',
        },
    },
    axisBackground: {
        stroke: 'url(#axisback)',
        strokeWidth: AXIS_THICKNESS,
    },
    axisForground: {
        stroke: 'url(#axisfor)',
        strokeWidth: FORGROUND_THICKNESS,
    },
    sliderContainer: {
        width: 250,
    },
    // decided not to use here - a little homemade looking?
    // keeping style just incase
    primaryToggle: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        paddingTop: 5,
        paddingBottom: 5,
        '&.Mui-selected': {
            color: theme.palette.getContrastText(theme.palette.primary.main),
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
        },
    },
    itemIcon: {
        minWidth: 30,
    },
    tableRow: {
        cursor: 'pointer',
    }
}));

const zoom = D3zoom().scaleExtent([1, 42]); //  limit scale

const alertsHeader = <TableHead>
    <TableRow>
        <TableCell>ATT&CK Category</TableCell>
        <TableCell>Src IP</TableCell>
        <TableCell>Src Port</TableCell>
        <TableCell>Dest IP</TableCell>
        <TableCell>Dest Port</TableCell>
        <TableCell>Proto</TableCell>
        <TableCell>Timestamp</TableCell>
    </TableRow>
</TableHead>;

const mtdtHeader = <TableHead>
    <TableRow>
        <TableCell>ATT&CK Category</TableCell>
        <TableCell>Orig IP</TableCell>
        <TableCell>Orig Port</TableCell>
        <TableCell>Resp IP</TableCell>
        <TableCell>Resp Port</TableCell>
        <TableCell>Proto</TableCell>
        <TableCell>Timestamp</TableCell>
    </TableRow>
</TableHead>;

const IncidentTimeline = ({ incident, onRemoveData, onUnlinkData, onUpdate, onSetMitre, onRemoveMitre }) => {
    const axisContainer = useRef(null);
    const zoomArea = useRef(null);
    const catgoryRef = useRef(null);
    const classes = useStyles();
    const { width } = useWindowSize();
    const { mitreCategoriesMap } = useContext(DataContext);
    const [updateIncident] = useState(Auth.permissions.get('Incidents').write);
    const [readonly] = useState(!(Auth.permissions.get('Incidents').write && Auth.permissions.get('Alerts').write));

    const [data, setData] = useState([]);
    const [start, setStart] = useState();
    const [end, setEnd] = useState();
    const [clusters, setClusters] = useState([]);
    const [superClusters, setSuperClusters] = useState([]);
    const [cwidth, setCWidth] = useState(Math.max(width - 3 * MARGIN, 500));
    const [transform, setTransform] = useState();
    const [scale, setScale] = useState(1);
    const [scan, setScan] = useState(0);
    const [rightend, setRightend] = useState(true);
    const [show, setShow] = useState({ note: true, data: ['alert', 'metadata'] });
    const [lockScale, setLockScale] = useState(false);
    const [showPopup, setShowPopup] = useState();
    const [addNoteOpen, setAddNoteOpen] = useState(false);
    const [xmargin, setXmargin] = useState(AXISMARGIN);

    const zoomed = useCallback((e, d) => {
        setTransform(e.transform);
    }, []);

    const sliderHandler = (e, v) => {
        zoom.scaleTo(D3select(zoomArea.current), v);
    };

    const sliderPlusHandler = (e, v) => {
        zoom.scaleBy(D3select(zoomArea.current).transition().duration(500), 2);
    };

    const sliderMinusHandler = (e, v) => {
        zoom.scaleBy(D3select(zoomArea.current).transition().duration(500), 0.5);
    };

    // lock scale to prevent the scale slider from bouncing during animation
    const sliderLeftHandler = e => {
        setLockScale(true);
        zoom.translateBy(
            D3select(zoomArea.current)
                .transition()
                .duration(500)
                .on('end', () => setLockScale(false))
            , 150, 0);
    };

    const sliderRightHandler = e => {
        setLockScale(true);
        zoom.translateBy(
            D3select(zoomArea.current)
                .transition()
                .duration(500)
                .on('end', () => setLockScale(false))
            , -150, 0);
    };

    const handleShowNotes = e => {
        setShow(current => ({ ...current, note: e.target.checked }));
    };

    const handleOnAddNote = n => {
        setAddNoteOpen(n.timestamp);
    };

    const handleAddNote = n => {
        setAddNoteOpen(false);
        onUpdate && onUpdate({ data: { notes: [{ ...n, created: addNoteOpen }] } });
    };

    // node clicks bubbled up from child components
    const handleShowNodeMenu = (x, y, node, actions, table) => {
        // defense against the dark arts of permissions
        if (!actions || actions.length < 1) return;

        setShowPopup({
            anchorEl: node,
            x,
            y,
            table,
            actions,
        });
    };

    // timeline click
    const handleOnTimelineClick = e => {
        const innerWidth = Math.max(width - 120, 500);
        let x = D3scaleTime().range([xmargin, innerWidth - xmargin]).domain([start, end]);
        // adjust for current zoom
        if (transform) {
            x = transform.rescaleX(x);
        }
        const timestamp = x.invert(e.nativeEvent.offsetX);
        setShowPopup({
            anchorEl: e.currentTarget,
            x: e.nativeEvent.offsetX,
            y: e.nativeEvent.offsetY,
            actions: [{
                title: 'Add Note',
                callback: () => handleOnAddNote({ timestamp }),
                icon: <NoteAddIcon fontSize="small" />
            }]
        });
    };

    const getCategory = useCallback(nodes => {
        const category = (nodes || [])
            .filter(n => n.mitre && mitreCategoriesMap[n.mitre])
            .reduce((a, c) => !a || !a.id ? mitreCategoriesMap[c.mitre]
                : mitreCategoriesMap[c.mitre].order > a.order
                    ? mitreCategoriesMap[c.mitre] : a, {});
        return category;
    }, [mitreCategoriesMap]);

    const calculateEnd = useCallback((incident) => {
        let max = (incident.alerts || []).reduce((a, c) => {
            return a.isAfter(c.timestamp) ? a : Moment(c.timestamp);
        }, Moment(incident.start));
        max = (incident.metadata || []).reduce((a, c) => {
            return a.isAfter(c.timestamp) ? a : Moment(c.timestamp);
        }, max);
        max = _get(incident, 'data.files', []).reduce((a, c) => {
            return a.isAfter(c.timestamp) ? a : Moment(c.timestamp);
        }, max);
        max = _get(incident, 'data.mitigations.external', []).reduce((a, c) => {
            return a.isAfter(c.timestamp) ? a : Moment(c.timestamp);
        }, max);
        max = _get(incident, 'data.mitigations.internal', []).reduce((a, c) => {
            return a.isAfter(c.timestamp) ? a : Moment(c.timestamp);
        }, max);
        max = _get(incident, 'data.notes', []).reduce((a, c) => {
            return a.isAfter(c.created) ? a : Moment(c.created);
        }, max);
        return max.add(3, 'hours').toDate();
    }, []);

    // top level pan and scan zoom
    useEffect(() => {
        if (zoomArea.current) {
            const z = zoom
                .translateExtent([[0, 0], [cwidth, 0]]) // limit x scan area
                .constrain((transform, extent, translateExtent) => {
                    const dx0 = transform.invertX(extent[0][0]) - translateExtent[0][0];
                    const dx1 = transform.invertX(extent[1][0]) - translateExtent[1][0];
                    const newx = dx1 > dx0
                        ? (dx0 + dx1) / 2 // full scale
                        : Math.min(0, dx0) || Math.max(0, dx1); // zooomed
                    const ret = transform.translate(newx, 0);

                    // (╯°□°）╯︵ ┻━┻
                    // end of the road test (or close enough)
                    setRightend(dx0 > 0 && transform.x !== ret.x);

                    return ret;
                })
                .on('zoom', zoomed);
            D3select(zoomArea.current).call(z);
        }
    }, [zoomed, cwidth]);

    // data
    useEffect(() => {
        const start = Moment(incident.start).subtract(3, 'hours').toDate();
        let end = incident.resolved
            ? new Date(incident.resolved) : calculateEnd(incident);

        if (Moment(end).diff(Moment(start), 'hours') < 24) {
            end = Moment(start).add(24, 'hours').toDate();
        }

        // figure out interval and round the end up
        const count = (innerWidth - 2 * MARGIN) / 130;
        const _interval = d3Time.timeTickInterval(start.getTime(), end.getTime(), count);
        end = _interval.ceil(end);

        const raw = [
            ...(_get(incident, 'metadata', []) || []).map(d => ({
                ...d,
                timestamp: new Date(_get(d, 'data._source.timestamp', '')),
                key: _get(d, 'data._source.bricata.event_uuid', ''),
                type: 'metadata',
                y: 2 * HEIGHT,
                mitre: _get(d, 'data._source.bricata.mitre_category', ''),
                fivetuple: [
                    _get(d, 'data._source.bro_log', {})['id.orig_h'],
                    _get(d, 'data._source.bro_log', {})['id.orig_p'],
                    _get(d, 'data._source.bro_log', {})['id.resp_h'],
                    _get(d, 'data._source.bro_log', {})['id.resp_p'],
                    _get(d, 'data._source.bro_log.proto'),
                ],
            })),
            ...(_get(incident, 'alerts', []) || []).map(d => ({
                ...d,
                timestamp: new Date(d.timestamp),
                key: d.uuid,
                type: 'alert',
                y: 0,
                mitre: _get(d, 'data.bricata.mitre_category', ''),
                fivetuple: [
                    _get(d, 'data.src_ip'),
                    _get(d, 'data.src_port'),
                    _get(d, 'data.dest_ip'),
                    _get(d, 'data.dest_port'),
                    _get(d, 'data.proto'),
                ],
            })),
            ...(_get(incident, 'data.notes', []) || []).map(d => ({
                ...d,
                timestamp: new Date(d.created),
                key: d.id,
                type: 'note',
                y: HEIGHT / 2,
            }))
        ];
        raw.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        setData(raw);
        setStart(start);
        setEnd(end);
    }, [incident, calculateEnd]);

    // layout
    useEffect(() => {
        if (!width || !start || !end) return;

        // timeline width grows without rounded corners
        const _xmargin = _get(transform, 'k', 1) === 1 ? AXISMARGIN : AXISMARGIN - 45;
        setXmargin(_xmargin);

        const innerWidth = Math.max(width - 120, 500);
        let x = D3scaleTime().domain([start, end]).range([_xmargin, innerWidth - _xmargin]);
        let _start = start;
        let _end = end;

        // adjust for current zoom
        if (transform) {
            x = transform.rescaleX(x);
            _start = x.invert(x.range()[0]);
            _end = x.invert(x.range()[1]);
        }

        // figure out sensible interval
        const count = (innerWidth - 2 * MARGIN) / 130;
        const _interval = d3Time.timeTickInterval(_start.getTime(), _end.getTime(), count);

        // don't allow clumping to go outside time range
        const interval = t => {
            return _interval(t).getTime() < _start.getTime() ? _start
                : _interval(t).getTime() > _end.getTime() ? _end
                    : _interval(t);
        };

        const raw = data
            .filter(d =>
                d.timestamp.getTime() >= _start.getTime() &&
                d.timestamp.getTime() <= _end.getTime()
            )
            .map(d => ({
                ...d,
                x: x(d.timestamp), // raw x position
                cluster: x(interval(d.timestamp)) // cluster x position
            }));

        // clump data into arrays of adjacent objects of the same type
        const clusters = Object.values(raw.reduce((a, c) => ({
            ...a,
            [`${c.cluster}_${c.type}`]: [...(a[`${c.cluster}_${c.type}`] || []), c]
        }), {}));

        // superClusters have both alerts and mtdt and are used for Mitre Category clustering
        const superClusters = Object.values(raw.reduce((a, c) => ({
            ...a,
            [c.cluster]: [...(a[c.cluster] || []), c]
        }), {}));

        // x-axis
        if (axisContainer.current) {
            D3select(axisContainer.current)
                .selectAll('g.x-axis')
                .data(['x-axis'])
                .join('g')
                .attr('class', 'x-axis')
                .call(D3axisBottom(x).tickPadding(FORGROUND_THICKNESS));
        }

        setClusters(clusters);
        setSuperClusters(superClusters);
        setCWidth(innerWidth);
        !lockScale && setScale(transform ? transform.k : 1);
        setScan(transform ? transform.x : 0);
    }, [width, data, end, start, transform, lockScale]);

    // category collision resolver - display highest category when collide
    // 2D collision detection inspiration
    // https://developer.mozilla.org/en-US/docs/Games/Techniques/2D_collision_detection
    useEffect(() => {
        if (!catgoryRef.current) return;
        const all = catgoryRef.current.querySelectorAll('div');
        // first remove all previous 'hide' classes
        all.forEach(n => n.classList.remove('hide'));

        for (let i = 0; i < all.length; i++) {
            const a = all[i];
            const acat = _get(a, 'dataset.category', '');
            const ahidden = a.classList.contains('hide');
            if (ahidden || !acat) return;

            for (let j = 0; j < all.length; j++) {
                const b = all[j];
                const bcat = _get(b, 'dataset.category', '');
                const bhidden = b.classList.contains('hide');
                if (a === b || bhidden || !bcat) continue;
                const arect = a.getBoundingClientRect();
                const brect = b.getBoundingClientRect();

                // we only care about x collisions
                if (arect.left < brect.left + brect.width &&
                  arect.left + arect.width > brect.left) {
                    // have a collision
                    if (acat < bcat) {
                        a.classList.add('hide');
                        break;
                    }
                    b.classList.add('hide');
                }
            }
        }
    }, [clusters]);

    return (
        <Box className={classes.root} style={{ marginBottom: 30 }}>
            <div ref={zoomArea} style={{ height: HEIGHT * 2 + AXIS_THICKNESS + MARGIN }}>
                {/* data */}
                <div className={classes.nodes}>
                    {clusters
                        .filter(d => show[d[0].type] || show.data.find(v => v === d[0].type))
                        .map(d =>
                            <Node
                                key={d[0].key}
                                position={{
                                    left: d.length < 1 ? d[0].x : d[0].cluster,
                                    top: d[0].y
                                }}
                                data={d}
                                onShowPopup={readonly ? undefined : handleShowNodeMenu}
                                onAddNote={handleOnAddNote}
                                onRemoveData={onRemoveData}
                                onUnlinkData={onUnlinkData}
                                onUpdate={onUpdate}
                                onSetMitre={onSetMitre}
                                onRemoveMitre={onRemoveMitre}/>
                        )}
                </div>

                {/* x-axis */}
                <div className={classes.axisContainer}>
                    <svg
                        className={classes.axis}
                        width={cwidth}
                        height={AXIS_THICKNESS}
                        ref={axisContainer}
                        onClick={updateIncident ? handleOnTimelineClick : undefined}>
                        <defs>
                            <linearGradient id="axisback" gradientUnits="userSpaceOnUse">
                                <stop offset="5%" stopColor={blue[400]} />
                                <stop offset="95%" stopColor={teal[400]} />
                            </linearGradient>
                            <linearGradient id="axisfor" gradientUnits="userSpaceOnUse">
                                <stop offset="5%" stopColor={purple[400]} />
                                <stop offset="95%" stopColor={purple[200]} />
                            </linearGradient>
                        </defs>
                        {/* background */}
                        <line x1={xmargin} y1="50%" x2={cwidth - xmargin} y2="50%"
                            className={classes.axisBackground}
                            strokeLinecap={scale === 1 ? 'round' : 'butt'}/>
                        {/* category stems */}
                        <g>
                            {superClusters
                                .filter(d => (getCategory(d) || {}).name)
                                .map((d, i) =>
                                    <line
                                        key={d[0].key}
                                        className={classes.categoryStem}
                                        x1={(d.length < 1 ? d[0].x : d[0].cluster)}
                                        x2={(d.length < 1 ? d[0].x : d[0].cluster)}
                                        y1={5} y2={AXIS_THICKNESS - 5}/>
                                )}
                        </g>
                        {/* forground */}
                        <line x1={scale === 1 ? 0 : xmargin} x2={cwidth - (scale === 1 ? 0 : xmargin)}
                            y1="50%" y2="50%"
                            className={classes.axisForground}/>
                    </svg>
                </div>
            </div>

            {/* category labels */}
            <div ref={catgoryRef}>
                {superClusters
                    .filter(d => (getCategory(d) || {}).name)
                    .map((d, i) => {
                        const cat = getCategory(d);
                        if (!cat || !cat.id) return null;
                        return <div key={d[0].key}>
                            {/* category label */}
                            <StyledTooltip title={cat.description}>
                                <div
                                    data-category={cat.id}
                                    className={classes.nodeCategory}
                                    style={{
                                        left: (d.length < 1 ? d[0].x : d[0].cluster) - 5,
                                        top: HEIGHT + AXIS_THICKNESS / 2 - 7,
                                    }}> {cat.name}
                                </div>
                            </StyledTooltip>
                        </div>;
                    })}
            </div>

            {/* popup menu */}
            <Popover
                style={{ maxHeight: 250 }}
                id="popover-with-anchor"
                open={Boolean(showPopup)}
                anchorEl={showPopup ? showPopup.anchorEl : undefined}
                onClose={() => setShowPopup()}
                anchorOrigin={{
                    vertical: showPopup ? showPopup.y : 'bottom',
                    horizontal: showPopup ? showPopup.x : 'center'
                }}>
                {showPopup && <StyledPaper className={classes.paper}>
                    {showPopup.table
                        ? <TableContainer>
                            <Table className={classes.table} size="small">
                                {showPopup.table === 'alerts' ? alertsHeader : mtdtHeader }
                                <TableBody>
                                    {_get(showPopup, 'actions', []).map((a, i) => (
                                        <TableRow
                                            hover
                                            className={classes.tableRow}
                                            key={`${a.title}_${i}`}
                                            onClick={e => {
                                                setShowPopup();
                                                a.callback(e);
                                            }}>
                                            <TableCell key='mitre'>
                                                <Mitre mitre={a.mitre} noclick={true}/>
                                            </TableCell>
                                            {a.columns.map((c, i) =>
                                                <TableCell key={`${c}_${i}`}>{c}</TableCell>
                                            )}
                                            <TableCell key='timestamp'>{a.timestamp}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        : _get(showPopup, 'actions', []).map((a, i) =>
                            <MenuItem
                                style={{ maxWidth: 300, whiteSpace: 'break-spaces' }}
                                key={`${a.title}_${i}`}
                                onClick={e => {
                                    setShowPopup();
                                    a.callback(e);
                                }}>
                                {a.icon && <ListItemIcon className={classes.itemIcon}>
                                    {a.icon}
                                </ListItemIcon>}
                                <ListItemText
                                    primary={
                                        <React.Fragment>
                                            <span>{a.title} </span>
                                            <span style={{ fontSize: 'small', float: 'right' }}>
                                                {a.info}</span>
                                        </React.Fragment>
                                    }
                                    secondary={a.desc} />
                            </MenuItem>
                        )
                    }
                </StyledPaper>}
            </Popover>

            {/* toolbar */}
            <div>
                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    direction="row"
                    wrap="nowrap">
                    <Grid item>
                        <FormControlLabel
                            control={<Switch
                                color="secondary"
                                checked={show.note}
                                onChange={handleShowNotes}
                                name="note"
                                inputProps={{ 'aria-label': 'show Notes' }}
                            />}
                            label="Notes"
                            labelPlacement="start"
                        />
                    </Grid>
                    <Grid container justifyContent="center" className={classes.sliderContainer}>
                        {/* slider */}
                        <Grid item>
                            <IconButton
                                disabled={scale <= 1 || scan >= 0}
                                onClick={sliderLeftHandler}
                                aria-label="scan left"
                                size="small">
                                <ChevronLeftIcon fontSize="inherit" />
                            </IconButton>
                        </Grid>
                        <Grid item>
                            <IconButton
                                disabled={scale <= 1}
                                onClick={sliderMinusHandler}
                                aria-label="zoom out"
                                size="small">
                                <RemoveIcon fontSize="inherit" />
                            </IconButton>
                        </Grid>
                        <Grid item xs>
                            <Slider
                                value={scale}
                                min={1}
                                max={42}
                                onChange={sliderHandler}
                                aria-label="scale" />
                        </Grid>
                        <Grid item>
                            <IconButton
                                disabled={scale >= 42}
                                onClick={sliderPlusHandler}
                                aria-label="zoom in"
                                size="small">
                                <AddIcon fontSize="inherit" />
                            </IconButton>
                        </Grid>
                        <Grid item>
                            <IconButton
                                disabled={scale <= 1 || rightend}
                                onClick={sliderRightHandler}
                                aria-label="scan right"
                                size="small">
                                <ChevronRightIcon fontSize="inherit" />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Grid>
            </div>

            <NewNote
                note={{ created: addNoteOpen || new Date() }}
                addNoteOpen={Boolean(addNoteOpen)}
                disableAdd={false}
                handleAddNote={onUpdate ? handleAddNote : undefined}
                handleCancelNote={() => setAddNoteOpen(false)}/>
        </Box>
    );
};

IncidentTimeline.propTypes = {
    incident: PropTypes.object.isRequired,
    className: PropTypes.string,
    onUpdate: PropTypes.func,
    onRemoveData: PropTypes.func,
    onUnlinkData: PropTypes.func,
    onSetMitre: PropTypes.func,
    onRemoveMitre: PropTypes.func,
};

const Node = props => {
    const nodeRef = useRef(null);
    const theme = useTheme();
    const { mitreCategories } = useContext(DataContext);
    const [updateAlert] = useState(Auth.permissions.get('Alerts').write);
    const [updateIncident] = useState(Auth.permissions.get('Incidents').write);

    // cluster click handler
    const handleClick = (e, type) => {
        if (props.data.length === 1) return handleNodeClick(e, type, props.data[0]);

        props.onShowPopup(props.position.left, props.position.top, nodeRef.current,
            (props.data || []).map((d) => ({
                ...d,
                columns: d.fivetuple,
                timestamp: Moment(d.timestamp).format(theme.date.short),
                type,
                callback: e => handleNodeClick(e, type, d)
            })), type);
    };

    // single click handler
    const handleNodeClick = (e, type, node) => {
        const setMitre = {
            title: 'Set Mitre ATT&CK',
            callback: e => {
                const clear = node.mitre ? [{
                    icon: <ClearIcon fontSize="small" />,
                    title: `${node.mitre}`,
                    callback: () => props.onRemoveMitre({ type, uuid: node.key }),
                }] : [];
                return props.onShowPopup(
                    props.position.left,
                    props.position.top,
                    nodeRef.current,
                    clear.concat(
                        (mitreCategories || []).map(c => ({
                            title: c.name,
                            info: `${c.id}: ${c.description}`,
                            callback: () => props.onSetMitre({
                                type,
                                uuid: node.key,
                                mitre: c.id,
                            })
                        }))
                    )
                );
            },
            icon: <Icon fontSize="small" style={{ fontFamily: 'unset' }}>&</Icon>
        };
        const remove = {
            title: `Remove ${type === 'alerts' ? 'Alert' : 'Metadata'}`,
            callback: () => props.onUnlinkData(type, node.uuid || node.event_uuid),
            icon: <DeleteIcon fontSize="small" />
        };
        const actions = [];
        if (updateAlert) actions.push(setMitre);
        if (updateIncident) actions.push(remove);

        props.onShowPopup(props.position.left, props.position.top, nodeRef.current, actions);
    };

    return (
        <Box ref={nodeRef}>
            {{
                alert: <AlertNode
                    {...props }
                    onClick={props.onShowPopup ? e => handleClick(e, 'alerts') : undefined}/>,
                metadata: <MetadataNode
                    {...props }
                    onClick={props.onShowPopup ? e => handleClick(e, 'metadata') : undefined}/>,
                note: <NoteNode {...props }/>,
            }[props.data[0].type]}
        </Box>
    );
};
Node.propTypes = {
    data: PropTypes.array.isRequired,
    position: PropTypes.object.isRequired,
    onShowPopup: PropTypes.func,
    onRemoveData: PropTypes.func,
    onUnlinkData: PropTypes.func,
    onUpdate: PropTypes.func,
    onSetMitre: PropTypes.func,
    onRemoveMitre: PropTypes.func,
};

const AlertNode = props => {
    const classes = useStyles();
    const theme = useTheme();

    return (
        <Box>
            {/* stem */}
            <Divider
                orientation="vertical"
                className={classes.nodeLine}
                style={{
                    left: props.position.left,
                    top: props.position.top + 15,
                    height: HEIGHT - 15,
                    background: olive[400],
                    zIndex: BASE_ZINDEX,
                }}/>
            {/* circle */}
            <div
                className={classes.nodeMarker}
                style={{
                    left: props.position.left - 6.5,
                    top: props.position.top - 2,
                }}>
                <svg height="20" width="20">
                    <circle
                        onClick={props.onClick}
                        cx="8.8" cy="11" r="7"
                        stroke={olive[400]}
                        strokeWidth="4"
                        fill={props.data.length > 1
                            ? theme.palette.background.default
                            : olive[400]} />
                </svg>
            </div>
            {/* label */}
            <div className={classes.nodeText}
                style={{
                    left: props.position.left + 3,
                    top: props.position.top - 2,
                }}>
                {props.data.length > 1 ? `${props.data.length} Alerts` : 'Alert'}
            </div>
        </Box>
    );
};
AlertNode.propTypes = {
    position: PropTypes.object.isRequired,
    data: PropTypes.array.isRequired,
    onClick: PropTypes.func,
};

const MetadataNode = props => {
    const classes = useStyles();
    const theme = useTheme();

    return (
        <Box>
            {/* stem */}
            <Divider
                orientation="vertical"
                className={classes.nodeLine}
                style={{
                    left: props.position.left,
                    top: props.position.top - HEIGHT + AXIS_THICKNESS,
                    height: HEIGHT - 12,
                    background: teal[400]
                }}/>
            {/* circle */}
            <div
                className={classes.nodeMarker}
                style={{
                    left: props.position.left - 6.5,
                    top: props.position.top + AXIS_THICKNESS - 16,
                }}>
                <svg height="20" width="20">
                    <circle
                        onClick={props.onClick}
                        cx="8.8" cy="10" r="7"
                        stroke={teal[400]}
                        strokeWidth="4"
                        fill={props.data.length > 1
                            ? theme.palette.background.default
                            : teal[400]} />
                </svg>
            </div>

            {/* label */}
            <Box component="div" className={classes.nodeText} textOverflow="ellipsis"
                style={{
                    left: props.position.left + 3,
                    top: props.position.top + AXIS_THICKNESS - 15,
                }}>
                {props.data.length > 1 ? `${props.data.length} Metadata` : 'Metadata'}
            </Box>
        </Box>
    );
};

MetadataNode.propTypes = {
    position: PropTypes.object.isRequired,
    data: PropTypes.array.isRequired,
    onClick: PropTypes.func,
};

const NoteNode = props => {
    const classes = useStyles();
    const nodeRef = useRef(null);
    const theme = useTheme();
    const [updateIncident] = useState(Auth.permissions.get('Incidents').write);

    // single click handler
    const handleNodeClick = (e, data) => {
        props.onShowPopup(props.position.left, props.position.top, nodeRef.current, [
            {
                title: 'Delete Note',
                callback: () => props.onRemoveData({ notes: [{ id: data.id }] }),
                icon: <DeleteIcon fontSize="small" />
            }
        ]);
    };

    // cluster click handler
    const handleClick = e => {
        if (props.data.length === 1) return handleNodeClick(e, props.data[0]);

        props.onShowPopup(props.position.left, props.position.top, nodeRef.current,
            (props.data || []).map((d) => ({
                ...d,
                info: Moment(d.created).format(theme.date.short),
                callback: e => handleNodeClick(e, d)
            })));
    };

    return (
        <Box ref={nodeRef}>
            {/* stem */}
            <Divider
                orientation="vertical"
                className={classes.nodeLine}
                style={{
                    left: props.position.left,
                    top: props.position.top + 15,
                    height: HEIGHT / 2 - 15,
                    background: pumpkin[400],
                    zIndex: BASE_ZINDEX - 1 // note stems are behind alert stems
                }}/>
            {/* circle */}
            <div
                className={classes.nodeMarker}
                style={{
                    left: props.position.left - 7,
                    top: props.position.top,
                }}>
                <StyledTooltip title={props.data.length > 1 ? '' : props.data[0].desc}>
                    <svg height="20" width="20">
                        <circle
                            onClick={updateIncident ? handleClick : undefined}
                            cx="8.8" cy="10" r="7"
                            stroke={pumpkin[400]}
                            strokeWidth="4"
                            fill={props.data.length > 1
                                ? theme.palette.background.default
                                : pumpkin[400]} />
                    </svg>
                </StyledTooltip>
            </div>
            {/* label */}
            <div className={classes.nodeText}
                style={{
                    left: props.position.left + 3,
                    top: props.position.top + 1,
                    color: theme.palette.info.main,
                    fontSize: props.data.length > 1 ? 14 : 10,
                }}>
                {props.data.length > 1 ? `${props.data.length} Notes` : props.data[0].title}
            </div>
        </Box>
    );
};
NoteNode.propTypes = {
    position: PropTypes.object.isRequired,
    data: PropTypes.array.isRequired,
    onShowPopup: PropTypes.func,
    onRemoveData: PropTypes.func,
};

export default IncidentTimeline;
